<template>
  <div class="rule-group" :class="{ 'rule-group-nested': isNested, 'rule-group-root': $props.isRoot }">
    <div class="rule-group-header" v-if="showOperator || canRemove || isNested || $props.isRoot">
      <span v-if="$props.isRoot" class="rule-group-label rule-group-label-root">
        <i class="el-icon-s-operation"></i>
        Main Conditions
      </span>
      <span v-else-if="isNested" class="rule-group-label">
        <i class="el-icon-folder-opened"></i>
        Nested Group
      </span>
      <el-select
        v-if="showOperator || $props.isRoot"
        v-model="localGroup.op"
        class="rule-group-operator"
        @change="onOperatorChange"
      >
        <el-option label="AND" value="AND"></el-option>
        <el-option label="OR" value="OR"></el-option>
      </el-select>
      <span v-if="$props.isRoot" class="rule-group-help-text">
        {{ showOperator ? 'Members must match' : 'Add rules or groups below' }}
      </span>
      <el-button
        v-if="canRemove"
        type="text"
        icon="el-icon-delete"
        class="rule-group-delete"
        @click="$emit('remove')"
      ></el-button>
    </div>

    <div class="rule-group-content">
      <div
        v-for="(child, index) in localGroup.children"
        :key="child.id || index"
        class="rule-group-item"
      >
        <!-- Render nested group -->
        <rule-group
          v-if="child.op"
          :group="child"
          :is-nested="true"
          :can-remove="true"
          :available-attributes="availableAttributes"
          :blur-mode="blurMode"
          @update="onChildUpdate(index, $event)"
          @remove="removeChild(index)"
        ></rule-group>

        <!-- Render rule row -->
        <rule-row
          v-else
          :rule="child"
          :available-attributes="availableAttributes"
          :blur-mode="blurMode"
          @update="onChildUpdate(index, $event)"
          @remove="removeChild(index)"
        ></rule-row>
      </div>

    </div>

    <div class="rule-group-footer">
      <el-button
        type="text"
        icon="el-icon-plus"
        class="rule-group-add-btn"
        @click="addRule"
      >
        Add Rule
      </el-button>
      <el-button
        type="text"
        icon="el-icon-plus"
        class="rule-group-add-btn"
        @click="addGroup"
      >
        Add Group
      </el-button>
    </div>
  </div>
</template>

<script>
import RuleRow from './RuleRow.vue';

export default {
  name: 'RuleGroup',
  components: {
    RuleRow,
  },
  props: {
    group: {
      type: Object,
      required: true,
    },
    isNested: {
      type: Boolean,
      default: false,
    },
    isRoot: {
      type: Boolean,
      default: false,
    },
    canRemove: {
      type: Boolean,
      default: true,
    },
    availableAttributes: {
      type: Array,
      required: true,
    },
    blurMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localGroup: { ...this.group },
    };
  },
  computed: {
    showOperator() {
      // Only show AND/OR operator when there are 2 or more rules/groups to combine
      return this.localGroup.children && this.localGroup.children.length >= 2;
    },
  },
  watch: {
    group: {
      handler(newGroup) {
        this.localGroup = { ...newGroup };
      },
      deep: true,
    },
  },
  methods: {
    onOperatorChange() {
      this.emitChange();
    },
    onChildUpdate(index, updatedChild) {
      this.$set(this.localGroup.children, index, updatedChild);
      this.emitChange();
    },
    addRule() {
      const newRule = {
        id: this.generateId(),
        field: '',
        operator: '',
        value: undefined,
      };
      this.localGroup.children.push(newRule);
      this.emitChange();
    },
    addGroup() {
      const newGroup = {
        id: this.generateId(),
        op: 'AND',
        children: [
          {
            id: this.generateId(),
            field: '',
            operator: '',
            value: undefined,
          },
        ],
      };
      this.localGroup.children.push(newGroup);
      this.emitChange();
    },
    removeChild(index) {
      this.localGroup.children.splice(index, 1);
      // Ensure at least one child exists
      if (this.localGroup.children.length === 0) {
        this.addRule();
      }
      this.emitChange();
    },
    emitChange() {
      this.$emit('update', this.localGroup);
    },
    generateId() {
      // Simple ID generator
      return `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.rule-group {
  border: 2px solid #e4e4e4;
  border-radius: 8px;
  padding: 14px;
  background-color: #fafafa;
  margin-bottom: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #d0d0d0;
  }

  &.rule-group-root {
    background-color: #f8f9fc;
    border-color: #8b92a8;
    border-width: 2px;
    padding: 16px;
    margin-bottom: 0;
    
    &:hover {
      border-color: #6b7280;
    }
    
    .rule-group-header {
      margin-bottom: 12px;
      padding-bottom: 10px;
      border-bottom-color: #d1d5db;
    }
  }

  &.rule-group-nested {
    background-color: #ffffff;
    border-color: #32c2d5;
    border-width: 2px;
    border-left-width: 4px;
    padding: 16px;
    margin-left: 20px;
    box-shadow: 0 2px 8px rgba(50, 194, 213, 0.08);
    
    &:hover {
      border-color: #2ab3c4;
      box-shadow: 0 2px 12px rgba(50, 194, 213, 0.12);
    }
    
    .rule-group-header {
      border-bottom-color: rgba(50, 194, 213, 0.2);
    }
  }

  .rule-group-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e4e4e4;

    .rule-group-label {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      background-color: #32c2d5;
      color: #ffffff;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border-radius: 4px;
      box-shadow: 0 2px 4px rgba(50, 194, 213, 0.2);
      
      i {
        font-size: 14px;
      }
      
      &.rule-group-label-root {
        background-color: #6b7280;
        font-size: 13px;
        padding: 5px 14px;
        box-shadow: 0 2px 4px rgba(107, 114, 128, 0.2);
      }
    }
    
    .rule-group-help-text {
      color: #6b7280;
      font-size: 13px;
      font-weight: 500;
      font-style: italic;
      margin-left: auto;
      opacity: 0.8;
    }

    .rule-group-operator {
      width: 110px;
      font-weight: 600;
      font-size: 15px;
    }

    .rule-group-delete {
      margin-left: auto;
      color: #f56c6c;
      padding: 6px 10px;
      font-size: 16px;
      transition: all 0.2s ease;

      &:hover {
        color: #ffffff;
        background-color: #f56c6c;
        border-radius: 4px;
        transform: scale(1.05);
      }
    }
  }

  .rule-group-content {
    .rule-group-item {
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .rule-group-footer {
    display: flex;
    gap: 16px;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px dashed #d0d0d0;

    .rule-group-add-btn {
      color: #32c2d5;
      font-size: 14px;
      font-weight: 600;
      padding: 8px 12px;

      &:hover {
        color: #32c2d5;
        background-color: rgba(50, 194, 213, 0.08);
        border-radius: 4px;
      }

      i {
        font-weight: bold;
      }
    }
  }
}
</style>

